import { createContext, useCallback, useContext } from 'react';
import { buildQuery } from '../utils/buildQuery';
import useApiService from './ApiService';
import CONFIG from './CONFIG';
import {
  PostCallAnalysisChatsApi,
  PostCallAnalysisExportParams,
  PostCallAnalysisConfigApi,
  PostCallAnalysisConfigByIdApi,
  PostCallAnalysisConfigCreateApi,
  PostCallAnalysisConfigDetailApi,
  PostCallAnalysisConfigListApi,
  PostCallAnalysisConfigUpdateApi,
  PostCallAnalysisMetricApi,
  PostCallAnalysisMetricCreateApi,
  PostCallAnalysisMetricListApi,
  PostCallAnalysisMetricUpdateApi,
  PostCallAnalysisSummaryApi,
  PostCallAnalysisThreadResultsApi,
} from './PostCallAnalysisApiProviderTypes';

interface PostCallAnalysisApiContextValue {
  // Configs
  getConfigs: (skip?: number, limit?: number) => Promise<PostCallAnalysisConfigListApi>;
  getConfigById(configId: string, includeDetails: true): Promise<PostCallAnalysisConfigDetailApi>;
  getConfigById(configId: string, includeDetails?: false): Promise<PostCallAnalysisConfigApi>;
  getConfigById(configId: string, includeDetails?: boolean): Promise<PostCallAnalysisConfigByIdApi>;
  createConfig: (
    configData: PostCallAnalysisConfigCreateApi,
  ) => Promise<PostCallAnalysisConfigByIdApi>;
  updateConfig: (
    configId: string,
    configData: PostCallAnalysisConfigUpdateApi,
  ) => Promise<PostCallAnalysisConfigByIdApi>;
  deleteConfig: (configId: string) => Promise<void>;
  // Channels
  setChannels: (configId: string, channelIds: string[]) => Promise<PostCallAnalysisConfigApi>;
  addChannel: (configId: string, channelId: string) => Promise<PostCallAnalysisConfigApi>;
  removeChannel: (configId: string, channelId: string) => Promise<PostCallAnalysisConfigApi>;
  // Metrics
  getMetrics: (configId: string) => Promise<PostCallAnalysisMetricListApi>;
  getMetricById: (configId: string, metricId: string) => Promise<PostCallAnalysisMetricApi>;
  createMetric: (
    configId: string,
    metricData: PostCallAnalysisMetricCreateApi,
  ) => Promise<PostCallAnalysisMetricApi>;
  updateMetric: (
    configId: string,
    metricId: string,
    metricData: PostCallAnalysisMetricUpdateApi,
  ) => Promise<PostCallAnalysisMetricApi>;
  deleteMetric: (configId: string, metricId: string) => Promise<void>;
  // Analytics
  getAnalyticsSummary: (
    configId: string,
    params?: { start_date?: string; end_date?: string; filters?: string },
  ) => Promise<PostCallAnalysisSummaryApi>;
  getAnalyticsChats: (
    configId: string,
    params?: {
      start_date?: string;
      end_date?: string;
      skip?: number;
      limit?: number;
      filters?: string;
    },
  ) => Promise<PostCallAnalysisChatsApi>;
  getThreadAnalysisResults: (threadId: string) => Promise<PostCallAnalysisThreadResultsApi>;
  exportAnalytics: (configId: string, params: PostCallAnalysisExportParams) => Promise<void>;
}

const PostCallAnalysisApiContext = createContext<PostCallAnalysisApiContextValue | null>(null);

export function PostCallAnalysisApiProvider({ children }) {
  const { get, post, put, del } = useApiService(CONFIG.SERVER_CHANNEL);

  // =========================================================================
  // Configuration CRUD
  // =========================================================================

  const getConfigs = useCallback(
    async (skip: number = 0, limit: number = 100): Promise<PostCallAnalysisConfigListApi> => {
      try {
        const response = await get(`/v1/post-call-analysis?skip=${skip}&limit=${limit}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching post-call analysis configs:', error);
        throw error;
      }
    },
    [get],
  );

  const getConfigById = useCallback(
    async (
      configId: string,
      includeDetails: boolean = false,
    ): Promise<PostCallAnalysisConfigByIdApi> => {
      try {
        const response = await get(
          `/v1/post-call-analysis/${configId}?include_details=${includeDetails}`,
        );
        return response.data;
      } catch (error) {
        console.error('Error fetching post-call analysis config by ID:', error);
        throw error;
      }
    },
    [get],
  );

  const createConfig = useCallback(
    async (configData: PostCallAnalysisConfigCreateApi): Promise<PostCallAnalysisConfigByIdApi> => {
      try {
        const response = await post(`/v1/post-call-analysis`, configData);
        return response.data;
      } catch (error) {
        console.error('Error creating post-call analysis config:', error);
        throw error;
      }
    },
    [post],
  );

  const updateConfig = useCallback(
    async (
      configId: string,
      configData: PostCallAnalysisConfigUpdateApi,
    ): Promise<PostCallAnalysisConfigByIdApi> => {
      try {
        const response = await put(`/v1/post-call-analysis/${configId}`, configData);
        return response.data;
      } catch (error) {
        console.error('Error updating post-call analysis config:', error);
        throw error;
      }
    },
    [put],
  );

  const deleteConfig = useCallback(
    async (configId: string): Promise<void> => {
      try {
        const response = await del(`/v1/post-call-analysis/${configId}`);
        return response.data;
      } catch (error) {
        console.error('Error deleting post-call analysis config:', error);
        throw error;
      }
    },
    [del],
  );

  // =========================================================================
  // Channel Management
  // =========================================================================

  const setChannels = useCallback(
    async (configId: string, channelIds: string[]): Promise<PostCallAnalysisConfigApi> => {
      try {
        const response = await put(`/v1/post-call-analysis/${configId}/channels`, {
          channel_ids: channelIds,
        });
        return response.data;
      } catch (error) {
        console.error('Error setting channels:', error);
        throw error;
      }
    },
    [put],
  );

  const addChannel = useCallback(
    async (configId: string, channelId: string): Promise<PostCallAnalysisConfigApi> => {
      try {
        const response = await post(`/v1/post-call-analysis/${configId}/channels/${channelId}`);
        return response.data;
      } catch (error) {
        console.error('Error adding channel:', error);
        throw error;
      }
    },
    [post],
  );

  const removeChannel = useCallback(
    async (configId: string, channelId: string): Promise<PostCallAnalysisConfigApi> => {
      try {
        const response = await del(`/v1/post-call-analysis/${configId}/channels/${channelId}`);
        return response.data;
      } catch (error) {
        console.error('Error removing channel:', error);
        throw error;
      }
    },
    [del],
  );

  // =========================================================================
  // Metrics Management
  // =========================================================================

  const getMetrics = useCallback(
    async (configId: string): Promise<PostCallAnalysisMetricListApi> => {
      try {
        const response = await get(`/v1/post-call-analysis/${configId}/metrics`);
        return response.data;
      } catch (error) {
        console.error('Error fetching metrics:', error);
        throw error;
      }
    },
    [get],
  );

  const getMetricById = useCallback(
    async (configId: string, metricId: string): Promise<PostCallAnalysisMetricApi> => {
      try {
        const response = await get(`/v1/post-call-analysis/${configId}/metrics/${metricId}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching metric by ID:', error);
        throw error;
      }
    },
    [get],
  );

  const createMetric = useCallback(
    async (
      configId: string,
      metricData: PostCallAnalysisMetricCreateApi,
    ): Promise<PostCallAnalysisMetricApi> => {
      try {
        const response = await post(`/v1/post-call-analysis/${configId}/metrics`, metricData);
        return response.data;
      } catch (error) {
        console.error('Error creating metric:', error);
        throw error;
      }
    },
    [post],
  );

  const updateMetric = useCallback(
    async (
      configId: string,
      metricId: string,
      metricData: PostCallAnalysisMetricUpdateApi,
    ): Promise<PostCallAnalysisMetricApi> => {
      try {
        const response = await put(
          `/v1/post-call-analysis/${configId}/metrics/${metricId}`,
          metricData,
        );
        return response.data;
      } catch (error) {
        console.error('Error updating metric:', error);
        throw error;
      }
    },
    [put],
  );

  const deleteMetric = useCallback(
    async (configId: string, metricId: string): Promise<void> => {
      try {
        const response = await del(`/v1/post-call-analysis/${configId}/metrics/${metricId}`);
        return response.data;
      } catch (error) {
        console.error('Error deleting metric:', error);
        throw error;
      }
    },
    [del],
  );

  const getAnalyticsSummary = useCallback(
    async (
      configId: string,
      params: { start_date?: string; end_date?: string; filters?: string },
    ): Promise<PostCallAnalysisSummaryApi> => {
      try {
        const query = buildQuery(params);
        const response = await get(`/v1/post-call-analysis/${configId}/analytics/summary?${query}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching post-call analytics summary:', error);
        throw error;
      }
    },
    [get],
  );

  const getAnalyticsChats = useCallback(
    async (
      configId: string,
      params: {
        start_date?: string;
        end_date?: string;
        skip?: number;
        limit?: number;
        filters?: string;
      },
    ): Promise<PostCallAnalysisChatsApi> => {
      try {
        const queryParams = buildQuery(params);
        const response = await get(
          `/v1/post-call-analysis/${configId}/analytics/chats?${queryParams}`,
        );
        return response.data;
      } catch (error) {
        console.error('Error fetching post-call analytics chats:', error);
        throw error;
      }
    },
    [get],
  );

  const getThreadAnalysisResults = useCallback(
    async (threadId: string): Promise<PostCallAnalysisThreadResultsApi> => {
      try {
        const response = await get(`/v1/post-call-analysis/threads/${threadId}/results`);
        return response.data;
      } catch (error) {
        console.error('Error fetching thread analysis results:', error);
        throw error;
      }
    },
    [get],
  );

  const exportAnalytics = useCallback(
    async (configId: string, params: PostCallAnalysisExportParams): Promise<void> => {
      try {
        const search = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value === undefined || value === null) return;
          if (Array.isArray(value)) {
            value.forEach((item) => search.append(key, String(item)));
          } else {
            search.append(key, String(value));
          }
        });

        const response = await get(
          `/v1/post-call-analysis/${configId}/analytics/export?${search.toString()}`,
          { responseType: 'blob' },
        );

        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'post_call_analytics.xlsx';
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error exporting post-call analytics:', error);
        throw error;
      }
    },
    [get],
  );

  return (
    <PostCallAnalysisApiContext.Provider
      value={{
        // Configs
        getConfigs,
        getConfigById: getConfigById as PostCallAnalysisApiContextValue['getConfigById'],
        createConfig,
        updateConfig,
        deleteConfig,
        // Channels
        setChannels,
        addChannel,
        removeChannel,
        // Metrics
        getMetrics,
        getMetricById,
        createMetric,
        updateMetric,
        deleteMetric,
        getAnalyticsSummary,
        getAnalyticsChats,
        getThreadAnalysisResults,
        exportAnalytics,
      }}
    >
      {children}
    </PostCallAnalysisApiContext.Provider>
  );
}

export function usePostCallAnalysisApi(): PostCallAnalysisApiContextValue {
  const context = useContext(PostCallAnalysisApiContext);
  if (!context) {
    throw new Error('usePostCallAnalysisApi must be used within a PostCallAnalysisApiProvider');
  }
  return context;
}
